import { FC, useState, useEffect } from 'react';
import { Grid, Button, Box, Typography, Chip, CircularProgress } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  ILessonPlan,
  ELessonPlanStatus,
} from '../../../../../apis/academics/academicsLessonPlans.interfaces';
import {
  useAcademicsLessonPlansState,
  useAcademicsLessonPlansActions,
} from '../../contexts/academicsLessonPlans.context';
import { ArrowBack, Delete } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import AcademicsLessonPlanDetailsView from './academicsLessonPlanDetails.view';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsLessonPlansKey } from '../../hooks/useAcademicsLessonPlansHook';

enum ELessonPlanView {
  LIST = 'list',
  DETAILS = 'details',
}

const AcademicsLessonPlansGridView: FC = () => {
  const { translateText } = useTranslation();
  const {
    filteredLessonPlans,
    totalCount,
    paginationParams,
    isLoading,
    selectedLessonPlan,
    selectedLessonPlanId,
  } = useAcademicsLessonPlansState();
  const {
    updatePaginationParams,
    setSelectedLessonPlanId,
    setSelectedLessonPlan,
    openAddMode,
    openEditMode,
  } = useAcademicsLessonPlansActions();
  const [viewType, setViewType] = useState<ELessonPlanView>(
    ELessonPlanView.LIST
  );

  // Sync viewType with context state
  useEffect(() => {
    if (selectedLessonPlan) {
      setViewType(ELessonPlanView.DETAILS);
    } else if (!selectedLessonPlanId) {
      setViewType(ELessonPlanView.LIST);
    }
  }, [selectedLessonPlan, selectedLessonPlanId]);

  useEffect(() => {
    if (selectedLessonPlanId && !selectedLessonPlan) {
      const lessonPlan = filteredLessonPlans.find(
        (lp) => lp.id === selectedLessonPlanId
      );
      if (lessonPlan) {
        setSelectedLessonPlan(lessonPlan);
      }
    }
    // eslint-disable-next-line
  }, [selectedLessonPlanId, filteredLessonPlans]);

  const getLessonPlanGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'title',
        headerName: translateText('academics.lessonPlans.columns.title'),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'subject',
        headerName: translateText('academics.lessonPlans.columns.subject'),
        width: 150,
      },
      {
        field: 'gradeLevel',
        headerName: translateText('academics.lessonPlans.columns.gradeLevel'),
        width: 120,
      },
      {
        field: 'academicYear',
        headerName: translateText('academics.lessonPlans.columns.academicYear'),
        width: 150,
      },
      {
        field: 'board',
        headerName: translateText('academics.lessonPlans.columns.board'),
        width: 120,
      },
      {
        field: 'status',
        headerName: translateText('academics.lessonPlans.columns.status'),
        width: 130,
        renderCell: (params) => {
          const status = params.value as ELessonPlanStatus;
          const statusColors: Record<
            ELessonPlanStatus,
            'default' | 'success' | 'warning' | 'error' | 'info'
          > = {
            [ELessonPlanStatus.DRAFT]: 'default',
            [ELessonPlanStatus.IN_REVIEW]: 'info',
            [ELessonPlanStatus.APPROVED]: 'success',
            [ELessonPlanStatus.ACTIVE]: 'success',
            [ELessonPlanStatus.ARCHIVED]: 'default',
          };
          return (
            <Chip
              label={status}
              color={statusColors[status] || 'default'}
              size="small"
            />
          );
        },
      },
      {
        field: 'totalDuration',
        headerName: translateText('academics.lessonPlans.columns.duration'),
        width: 120,
        renderCell: (params) => {
          return `${params.value} ${translateText('academics.lessonPlans.days')}`;
        },
      },
      {
        field: 'actions',
        headerName: translateText('academics.lessonPlans.columns.createdAt'),
        width: 150,
        renderCell: (params: any) => {
          const data = params.row as ILessonPlan;
          if (!data?.actions?.created?.timestamp) return '';
          try {
            return new Date(
              data.actions.created.timestamp
            ).toLocaleDateString();
          } catch {
            return '';
          }
        },
      },
    ];
  };

  const handleRowClick = (params: any) => {
    if (viewType === ELessonPlanView.LIST) {
      const lessonPlan = filteredLessonPlans.find(
        (lp) => lp.id === params.row.id
      );
      if (lessonPlan) {
        setSelectedLessonPlanId(lessonPlan.id);
        setSelectedLessonPlan(lessonPlan);
        setViewType(ELessonPlanView.DETAILS);
      }
    }
  };

  const handleBackToList = () => {
    setViewType(ELessonPlanView.LIST);
    setSelectedLessonPlan(null);
    setSelectedLessonPlanId(null);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    updatePaginationParams({ page, limit: pageSize });
  };

  const handleEditLessonPlan = () => {
    if (selectedLessonPlan) {
      openEditMode(selectedLessonPlan);
    }
  };

  const { deleteLessonPlan } = useAcademicsService();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteLessonPlan = async () => {
    if (!selectedLessonPlan) return;
    setIsDeleting(true);
    try {
      await deleteLessonPlan(selectedLessonPlan.id);
      queryClient.invalidateQueries({ queryKey: [getAcademicsLessonPlansKey] });
      handleBackToList();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddLessonPlan = () => {
    openAddMode();
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="academics-lesson-plans-grid-container"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 },
          mb: { xs: 1, sm: 2 },
        }}
        data-testid="lesson-plans-header-container"
      >
        {viewType === ELessonPlanView.DETAILS && selectedLessonPlan ? (
          <>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              gap={{ xs: 1, sm: 0 }}
              data-testid="lesson-plan-detail-header"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToList}
                sx={{
                  mr: { xs: 0, sm: 2 },
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="back-to-list-button"
              >
                {translateText('academics.lessonPlans.backToList')}
              </Button>
              <Typography variant="h6" data-testid="detail-header-title" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
                {selectedLessonPlan.title ||
                  translateText('academics.lessonPlans.nA')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <EditButton
                label={translateText('academics.lessonPlans.editLessonPlan')}
                onClickHandler={handleEditLessonPlan}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
                onClick={handleDeleteLessonPlan}
                disabled={isDeleting}
                sx={{ minHeight: { xs: '44px', sm: '36px' }, fontSize: { xs: '16px', sm: '14px' } }}
                data-testid="delete-lesson-plan-button"
              >
                {translateText('academics.lessonPlans.deleteLessonPlan') || 'Delete'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" data-testid="lesson-plans-header" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {translateText('academics.lessonPlans.title')}
            </Typography>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <AddButton
                label={translateText('academics.lessonPlans.addLessonPlan')}
                onClickHandler={handleAddLessonPlan}
              />
            </Box>
          </>
        )}
      </Grid>

      {viewType === ELessonPlanView.DETAILS && selectedLessonPlan && (
        <Grid
          item
          xs={12}
          sx={{ mb: { xs: 1, sm: 2 } }}
          data-testid="lesson-plan-detail-info-container"
        >
          <AcademicsLessonPlanDetailsView />
        </Grid>
      )}

      {viewType === ELessonPlanView.LIST && (
        <Grid
          item
          xs={12}
          data-testid="lesson-plans-grid-wrapper"
          sx={{
            height: 'calc(100vh - 250px)',
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            data-testid="lesson-plans-grid"
            rows={filteredLessonPlans}
            columns={getLessonPlanGridColumns()}
            onRowClick={handleRowClick}
            loading={isLoading}
            serverSide={true}
            totalCount={totalCount}
            currentPage={paginationParams.page}
            currentPageSize={paginationParams.limit}
            onPageChange={handlePageChange}
            hideColumnsOnMobile={['board', 'academicYear', 'gradeLevel']}
            getRowId={(row) => {
              if (!row || !row.id) {
                console.warn('Invalid row data:', row);
                return '';
              }
              return row.id;
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AcademicsLessonPlansGridView;
